import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePlayerPopup } from '../contexts/PlayerPopupContext';
import { getRankInfo } from '../rank';

const API = process.env.REACT_APP_API_URL || '';

const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'D.C.',
};

function toTitleCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function AvatarFallback({ name, size = 34 }) {
  const initial = (name ?? '?').charAt(0).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'var(--cb-navy)', border: '2px solid var(--cb-gray-200)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.38, fontWeight: 700, color: 'white', flexShrink: 0,
    }}>{initial}</div>
  );
}

function LevelBadge({ rankInfo, size = 'md' }) {
  const fontSize = size === 'sm' ? 10 : 11;
  const iconSize = size === 'sm' ? 13 : 15;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 2,
      background: `${rankInfo.color}18`, border: `1.2px solid ${rankInfo.color}44`,
      borderRadius: 5, padding: size === 'sm' ? '1px 4px' : '1px 5px',
      fontSize, fontWeight: 800, color: rankInfo.color, flexShrink: 0,
      lineHeight: 1.4,
    }}>
      {rankInfo.level}
      <img src={rankInfo.icon} alt={rankInfo.label} style={{ width: iconSize, height: iconSize, objectFit: 'contain' }} />
    </span>
  );
}

function CompactRow({ p, rank, user, metaRight, isGhost }) {
  const isMe = p && user && p.id === user.id;
  const rankInfo = getRankInfo(p.rating ?? 0);
  const displayName = p.display_name || p.name;
  const { openPopup } = usePlayerPopup();
  const handleClick = (e) => openPopup(p.id, e.clientX, e.clientY);
  return (
    <div className={`lb-compact-row${isMe ? ' me' : ''}${isGhost ? ' ghost' : ''}`}>
      <div className={`lb-rank-num${rank <= 3 ? ' top' + rank : ''}`}>{rank}</div>
      <button className="lb-avatar-link lb-name-btn" onClick={handleClick}>
        {p.avatar
          ? <img src={p.avatar} alt="" className="lb-compact-avatar" />
          : <AvatarFallback name={displayName} size={34} />
        }
      </button>
      <div className="lb-compact-info">
        <button
          className="lb-compact-name lb-name-btn"
          style={{ color: rankInfo.color, display: 'flex', alignItems: 'center', gap: 5, minWidth: 0, overflow: 'hidden' }}
          onClick={handleClick}
        >
          <LevelBadge rankInfo={rankInfo} size="sm" />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {displayName}
          </span>
        </button>
        {p.school && <div className="lb-compact-school">{p.school}</div>}
      </div>
      <div className="lb-compact-metric">{metaRight}</div>
    </div>
  );
}

function BlockToggle({ options, active, onSelect }) {
  return (
    <div className="lb-block-toggle">
      {options.map(o => (
        <button
          key={o.id}
          className={`lb-block-tab${active === o.id ? ' active' : ''}${o.locked ? ' locked' : ''}`}
          onClick={() => !o.locked && onSelect(o.id)}
          title={o.locked ? 'Set a school flair to unlock' : ''}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const LB_RANK_CONFIG = {
  'LIFETIME EXP': { color: 'var(--cb-navy)' },
  'WINS':         { color: '#3c8cdc' },
  'LOGIN STREAK': { color: 'var(--cb-navy)' },
};

function LeaderboardBlock({ title, options, activeTab, onTabChange, subOptions, activeSubTab, onSubTabChange, players, loading, user, renderMetric, emptyMsg, myRank, myMetric }) {
  const top20 = (players || []).slice(0, 20);
  const userInList = user && top20.some(p => p.id === user.id);
  const showGhost = user && !userInList && myRank != null;
  const rankCfg = LB_RANK_CONFIG[title] || null;

  return (
    <div className="lb-block">
      <div className="lb-block-header">
        {rankCfg ? (
          <span className="lb-block-title" style={{
            fontFamily: "'Impact', 'Arial Narrow', sans-serif",
            fontSize: 38, fontWeight: 400, color: rankCfg.color,
          }}>
            {title}
          </span>
        ) : (
          <span className="lb-block-title">{title}</span>
        )}
        <BlockToggle options={options} active={activeTab} onSelect={onTabChange} />
        {subOptions && (
          <BlockToggle options={subOptions} active={activeSubTab} onSelect={onSubTabChange} />
        )}
      </div>
      <div className="lb-block-body">
        {loading ? (
          <div className="lb-empty">
            <div className="queue-spinner" style={{ margin: '0 auto 12px' }} />
            Loading…
          </div>
        ) : top20.length === 0 && !showGhost ? (
          <div className="lb-empty">{emptyMsg}</div>
        ) : (
          <>
            {top20.map((p, i) => (
              <CompactRow key={p.id} p={p} rank={i + 1} user={user} metaRight={renderMetric(p)} />
            ))}
            {showGhost && (
              <>
                <div className="lb-ghost-divider">· · ·</div>
                <CompactRow
                  p={{ ...user, name: user.display_name || user.name, school: user.school }}
                  rank={myRank}
                  user={user}
                  metaRight={myMetric}
                  isGhost
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Composite key for local wins data: "county_daily", "state_weekly", etc.
function localKey(loc, time) { return `${loc}_${time}`; }

export default function Leaderboard() {
  const { user } = useAuth();
  const hasSchool = !!user?.school;

  // Unified Wins state (Global + Local merged)
  const [winsLocTab,   setWinsLocTab]   = useState('global');
  const [winsTimeTab,  setWinsTimeTab]  = useState('daily');
  const [winsData,     setWinsData]     = useState({ daily: null, weekly: null, total: null });
  const [winsLoading,  setWinsLoading]  = useState({ daily: false, weekly: false, total: false });
  const [localData,    setLocalData]    = useState({});
  const [localLoading, setLocalLoading] = useState({});

  // Streak state
  const [streakTab,    setStreakTab]    = useState('global');
  const [streakData,   setStreakData]   = useState({ global: null, state: null, local: null });
  const [streakLoading,setStreakLoading]= useState({ global: false, state: false, local: false });

  // Lifetime XP state
  const [xpTab,     setXpTab]     = useState('global');
  const [xpData,    setXpData]    = useState({ global: null, state: null, local: null });
  const [xpLoading, setXpLoading] = useState({ global: false, state: false, local: false });

  const [myRanks, setMyRanks] = useState(null);

  const countyLabel = user?.school_county ? toTitleCase(user.school_county) + ' Co.' : 'Local';
  const stateLabel  = user?.school_state  ? (STATE_NAMES[user.school_state] || user.school_state) : 'State';

  const fetchOnce = useCallback((key, url, setter, loadSetter, transform) => {
    loadSetter(l => ({ ...l, [key]: true }));
    axios.get(url)
      .then(r => setter(d => ({ ...d, [key]: transform ? transform(r.data) : r.data })))
      .catch(() => setter(d => ({ ...d, [key]: [] })))
      .finally(() => loadSetter(l => ({ ...l, [key]: false })));
  }, []); // eslint-disable-line

  const fetchLocalOnce = useCallback((loc, time) => {
    const key = localKey(loc, time);
    setLocalLoading(l => ({ ...l, [key]: true }));
    const url = `${API}/api/leaderboard/wins/${loc}/${time}`;
    axios.get(url)
      .then(r => {
        const players = Array.isArray(r.data) ? r.data : (r.data.players ?? []);
        setLocalData(d => ({ ...d, [key]: players }));
      })
      .catch(() => setLocalData(d => ({ ...d, [key]: [] })))
      .finally(() => setLocalLoading(l => ({ ...l, [key]: false })));
  }, []); // eslint-disable-line

  // Fetch user's ranks once
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/api/my-rank`)
      .then(r => setMyRanks(r.data))
      .catch(() => {});
  }, [user]); // eslint-disable-line

  // Unified wins fetches
  useEffect(() => {
    if (winsLocTab === 'global') {
      if (winsTimeTab === 'daily'  && winsData.daily  === null) fetchOnce('daily',  `${API}/api/leaderboard/daily`,      setWinsData, setWinsLoading);
      if (winsTimeTab === 'weekly' && winsData.weekly === null) fetchOnce('weekly', `${API}/api/leaderboard/weekly`,     setWinsData, setWinsLoading);
      if (winsTimeTab === 'total'  && winsData.total  === null) fetchOnce('total',  `${API}/api/leaderboard/wins/total`, setWinsData, setWinsLoading);
    } else if (hasSchool) {
      const key = localKey(winsLocTab, winsTimeTab);
      if (localData[key] === undefined) fetchLocalOnce(winsLocTab, winsTimeTab);
    }
  }, [winsLocTab, winsTimeTab, hasSchool]); // eslint-disable-line

  // Streak fetches
  useEffect(() => {
    if (streakTab === 'global' && streakData.global === null) fetchOnce('global', `${API}/api/leaderboard/streaks`,        setStreakData, setStreakLoading);
    if (streakTab === 'state'  && streakData.state  === null && hasSchool) fetchOnce('state',  `${API}/api/leaderboard/streaks/state`,  setStreakData, setStreakLoading, d => d.players ?? []);
    if (streakTab === 'local'  && streakData.local  === null && hasSchool) fetchOnce('local',  `${API}/api/leaderboard/streaks/county`, setStreakData, setStreakLoading, d => d.players ?? []);
  }, [streakTab]); // eslint-disable-line

  // Lifetime XP fetches
  useEffect(() => {
    if (xpTab === 'global' && xpData.global === null) fetchOnce('global', `${API}/api/leaderboard`,        setXpData, setXpLoading);
    if (xpTab === 'state'  && xpData.state  === null && hasSchool) fetchOnce('state',  `${API}/api/leaderboard/state`,  setXpData, setXpLoading, d => d.players ?? d);
    if (xpTab === 'local'  && xpData.local  === null && hasSchool) fetchOnce('local',  `${API}/api/leaderboard/county`, setXpData, setXpLoading, d => d.players ?? d);
  }, [xpTab]); // eslint-disable-line

  // Initial loads
  useEffect(() => {
    fetchOnce('daily',  `${API}/api/leaderboard/daily`,   setWinsData, setWinsLoading);
    fetchOnce('global', `${API}/api/leaderboard/streaks`, setStreakData, setStreakLoading);
    fetchOnce('global', `${API}/api/leaderboard`,         setXpData, setXpLoading);
  }, []); // eslint-disable-line

  // Unified wins tab options
  const winsLocOptions = [
    { id: 'global', label: 'Global',    locked: false },
    { id: 'state',  label: stateLabel,  locked: !hasSchool },
    { id: 'county', label: countyLabel, locked: !hasSchool },
  ];
  const winsTimeOptions = [
    { id: 'daily',  label: 'Daily',    locked: false },
    { id: 'weekly', label: 'Weekly',   locked: false },
    { id: 'total',  label: 'All-Time', locked: false },
  ];
  const streakOptions = [
    { id: 'global', label: 'Global',    locked: false },
    { id: 'state',  label: stateLabel,  locked: !hasSchool },
    { id: 'local',  label: countyLabel, locked: !hasSchool },
  ];
  const xpOptions = [
    { id: 'global', label: 'Global',    locked: false },
    { id: 'state',  label: stateLabel,  locked: !hasSchool },
    { id: 'local',  label: countyLabel, locked: !hasSchool },
  ];

  // Unified wins ghost row data
  const winsActivePlayers = winsLocTab === 'global'
    ? (winsData[winsTimeTab] ?? [])
    : (localData[localKey(winsLocTab, winsTimeTab)] ?? []);
  const winsActiveLoading = winsLocTab === 'global'
    ? winsLoading[winsTimeTab]
    : !!localLoading[localKey(winsLocTab, winsTimeTab)];
  const winsMyRank = myRanks ? (
    winsLocTab === 'global'
      ? { daily: myRanks.dailyRank,       weekly: myRanks.weeklyRank,       total: myRanks.totalWinsRank    }[winsTimeTab]
      : winsLocTab === 'state'
        ? { daily: myRanks.stateDailyRank,  weekly: myRanks.stateWeeklyRank,  total: myRanks.stateTotalWinsRank  }[winsTimeTab]
        : { daily: myRanks.countyDailyRank, weekly: myRanks.countyWeeklyRank, total: myRanks.countyTotalWinsRank }[winsTimeTab]
  ) : null;
  const winsMyMetricVal = myRanks ? (
    winsLocTab === 'global'
      ? { daily: myRanks.dailyWins,       weekly: myRanks.weeklyWins,       total: myRanks.totalWins }[winsTimeTab]
      : winsLocTab === 'state'
        ? { daily: myRanks.stateDailyWins,  weekly: myRanks.stateWeeklyWins,  total: myRanks.totalWins }[winsTimeTab]
        : { daily: myRanks.countyDailyWins, weekly: myRanks.countyWeeklyWins, total: myRanks.totalWins }[winsTimeTab]
  ) : null;

  // Streak ghost row data
  const streakMyRank = myRanks ? { global: myRanks.streakRankGlobal, state: myRanks.streakRankState, local: myRanks.streakRankCounty }[streakTab] : null;

  // XP ghost row data
  const xpMyRank = myRanks ? { global: myRanks.rankGlobal, state: myRanks.rankState, local: myRanks.rankCounty }[xpTab] : null;

  const xpMetric  = (p) => <span className="lb-metric-wins">{(p.rating ?? 0).toLocaleString()} <span className="lb-metric-label">XP</span></span>;
  const winsMetric = (p) => <span className="lb-metric-wins">{p.win_count} <span className="lb-metric-label">{parseInt(p.win_count) === 1 ? 'win' : 'wins'}</span></span>;

  const emptyXp   = { global: 'No players yet.', state: hasSchool ? `No players in ${stateLabel} yet.` : 'Set a school to unlock.', local: hasSchool ? `No players in ${countyLabel} yet.` : 'Set a school to unlock.' };
  const emptyWins = winsLocTab === 'global'
    ? { daily: 'No wins today yet.', weekly: 'No wins this week yet.', total: 'No wins recorded.' }[winsTimeTab]
    : !hasSchool ? 'Set a school to unlock local rankings.'
    : winsLocTab === 'state' ? `No wins in ${stateLabel} yet.`
    : `No wins in ${countyLabel} yet.`;
  const emptyStreak = { global: 'No streaks yet.', state: hasSchool ? `No streaks in ${stateLabel} yet.` : 'Set a school to unlock.', local: hasSchool ? `No streaks in ${countyLabel} yet.` : 'Set a school to unlock.' };

  return (
    <div className="page-wide lb-page">
      <div className="lb-three-col">

        {/* Column 1 — Lifetime EXP */}
        <LeaderboardBlock
          title="LIFETIME EXP"
          options={xpOptions}
          activeTab={xpTab}
          onTabChange={setXpTab}
          players={xpData[xpTab] ?? []}
          loading={xpLoading[xpTab]}
          user={user}
          renderMetric={xpMetric}
          emptyMsg={emptyXp[xpTab]}
          myRank={xpMyRank}
          myMetric={user ? xpMetric({ rating: user.rating ?? 0 }) : null}
        />

        {/* Column 2 — Wins (Global / State / Local) */}
        <LeaderboardBlock
          title="WINS"
          options={winsLocOptions}
          activeTab={winsLocTab}
          onTabChange={setWinsLocTab}
          subOptions={winsTimeOptions}
          activeSubTab={winsTimeTab}
          onSubTabChange={setWinsTimeTab}
          players={winsActivePlayers}
          loading={winsActiveLoading}
          user={user}
          renderMetric={winsMetric}
          emptyMsg={emptyWins}
          myRank={winsMyRank}
          myMetric={winsMyMetricVal != null ? winsMetric({ win_count: winsMyMetricVal }) : null}
        />

        {/* Column 3 — Login Streak */}
        <LeaderboardBlock
          title="LOGIN STREAK"
          options={streakOptions}
          activeTab={streakTab}
          onTabChange={setStreakTab}
          players={streakData[streakTab] ?? []}
          loading={streakLoading[streakTab]}
          user={user}
          renderMetric={p => <span className="lb-metric-streak">{p.login_streak}</span>}
          emptyMsg={emptyStreak[streakTab]}
          myRank={streakMyRank}
          myMetric={user ? <span className="lb-metric-streak">{user.login_streak ?? 0}</span> : null}
        />

      </div>
    </div>
  );
}
